import { useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, X, Plus, Upload, Link2, GripVertical, ArrowUp, ArrowDown, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absoluteUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);
const BODY_TYPES = ["Limousine", "Kombi", "SUV", "Coupé", "Cabrio", "Kleinwagen", "Van"];
const FUEL_TYPES = ["Benzin", "Diesel", "Elektro", "Hybrid", "LPG"];
const TRANSMISSIONS = ["Manuell", "Automatik"];

const empty = {
  make: "", model: "", year: new Date().getFullYear(), price: 0, mileage: 0,
  fuel_type: "Diesel", transmission: "Automatik", power_ps: 0, color: "",
  body_type: "Limousine", first_registration: "", description: "",
  equipment: [], images: [], featured: false, sold: false,
  tax_type: "differenz",
};

export default function VehicleForm({ vehicle, onClose, onSaved }) {
  const [form, setForm] = useState(vehicle || empty);
  const [newImg, setNewImg] = useState("");
  const [newEq, setNewEq] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadRef = useRef(null);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const addItem = (key, val, reset) => {
    if (!val.trim()) return;
    setForm({ ...form, [key]: [...(form[key] || []), val.trim()] });
    reset("");
  };
  const removeItem = (key, idx) => setForm({ ...form, [key]: form[key].filter((_, i) => i !== idx) });

  const moveImage = (from, to) => {
    setForm((prev) => {
      const imgs = [...(prev.images || [])];
      if (to < 0 || to >= imgs.length) return prev;
      const [moved] = imgs.splice(from, 1);
      imgs.splice(to, 0, moved);
      return { ...prev, images: imgs };
    });
  };

  const setAsCover = (idx) => moveImage(idx, 0);

  // Drag & drop reordering
  const dragIdx = useRef(null);
  const onDragStart = (i) => () => { dragIdx.current = i; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIdx.current == null || dragIdx.current === i) return;
    moveImage(dragIdx.current, i);
    dragIdx.current = null;
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let added = 0;
    for (const f of files) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: kein Bild`);
        continue;
      }
      if (f.size > 12 * 1024 * 1024) {
        toast.error(`${f.name}: zu groß (max. 12 MB)`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append("image", f);
        const res = await api.post("/admin/uploads/vehicle-image", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setForm((prev) => ({ ...prev, images: [...(prev.images || []), res.data.url] }));
        added += 1;
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    setUploading(false);
    if (added > 0) toast.success(`${added} Bild${added === 1 ? "" : "er"} hochgeladen`);
    if (uploadRef.current) uploadRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year), price: Number(form.price),
        mileage: Number(form.mileage), power_ps: Number(form.power_ps),
      };
      if (vehicle) {
        await api.put(`/admin/vehicles/${vehicle.id}`, payload);
        toast.success("Fahrzeug aktualisiert");
      } else {
        await api.post("/admin/vehicles", payload);
        toast.success("Fahrzeug angelegt");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-[calc(100vh-80px)]" data-testid="vehicle-form-page">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
          <button onClick={onClose} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-2" data-testid="form-back">
            <ArrowLeft size={14} /> Zurück zur Übersicht
          </button>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-slate-900">
            {vehicle ? "Fahrzeug bearbeiten" : "Neues Fahrzeug"}
          </h1>
        </div>
      </div>

      <form onSubmit={submit} className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-8">
        <Section title="Grunddaten">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Marke *" value={form.make} onChange={(v) => set("make", v)} required testId="f-make" />
            <Field label="Modell *" value={form.model} onChange={(v) => set("model", v)} required testId="f-model" />
            <Field label="Baujahr *" type="number" value={form.year} onChange={(v) => set("year", v)} required testId="f-year" />
            <Field label="Erstzulassung (MM/JJJJ) *" value={form.first_registration} onChange={(v) => set("first_registration", v)} required testId="f-firstreg" />
            <Field label="Preis (€) *" type="number" value={form.price} onChange={(v) => set("price", v)} required testId="f-price" />
            <Field label="Kilometerstand *" type="number" value={form.mileage} onChange={(v) => set("mileage", v)} required testId="f-mileage" />
            <Field label="Leistung (PS) *" type="number" value={form.power_ps} onChange={(v) => set("power_ps", v)} required testId="f-power" />
            <Field label="Farbe *" value={form.color} onChange={(v) => set("color", v)} required testId="f-color" />
            <SelectField label="Karosserie *" value={form.body_type} onChange={(v) => set("body_type", v)} options={BODY_TYPES} testId="f-body" />
            <SelectField label="Kraftstoff *" value={form.fuel_type} onChange={(v) => set("fuel_type", v)} options={FUEL_TYPES} testId="f-fuel" />
            <SelectField label="Getriebe *" value={form.transmission} onChange={(v) => set("transmission", v)} options={TRANSMISSIONS} testId="f-trans" />
          </div>
        </Section>

        <Section title="Beschreibung">
          <textarea rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="f-description"
            placeholder="Detaillierte Fahrzeugbeschreibung…"
            className="w-full border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:border-slate-900 resize-none bg-white" />
        </Section>

        <Section title="Bilder">
          {/* Upload area */}
          <label
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              uploadFiles(Array.from(e.dataTransfer.files || []));
            }}
            className="block border-2 border-dashed border-slate-300 hover:border-slate-900 cursor-pointer p-6 text-center transition-crisp bg-white"
            data-testid="f-upload-zone"
          >
            <input
              ref={uploadRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))}
              data-testid="f-upload-input"
            />
            <Upload size={24} strokeWidth={1.4} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm font-medium text-slate-900">
              {uploading ? "Wird hochgeladen…" : "Bilder vom Computer hochladen"}
            </p>
            <p className="text-xs text-slate-500 mt-1">Mehrere Bilder gleichzeitig möglich · JPG / PNG / WebP · max. 12 MB pro Bild</p>
          </label>

          {/* Optional URL input */}
          <details className="mt-4">
            <summary className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer inline-flex items-center gap-1.5">
              <Link2 size={12} /> Stattdessen Bild-URL einfügen
            </summary>
            <div className="flex gap-2 mt-3">
              <input value={newImg} onChange={(e) => setNewImg(e.target.value)} data-testid="f-newimg" placeholder="https://…"
                className="flex-1 border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900 bg-white" />
              <button type="button" onClick={() => addItem("images", newImg, setNewImg)} data-testid="f-addimg" className="bg-slate-900 text-white px-4 text-sm hover:bg-red-600 transition-crisp">
                <Plus size={16} />
              </button>
            </div>
          </details>

          {form.images?.length > 0 && (
            <>
              <div className="flex items-center justify-between mt-5 mb-2">
                <p className="text-xs text-slate-500">
                  {form.images.length} Bild{form.images.length === 1 ? "" : "er"} · Ziehe ein Bild an die gewünschte Stelle, das erste ist das Titelbild
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {form.images.map((img, i) => (
                  <div
                    key={`${img}-${i}`}
                    draggable
                    onDragStart={onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={onDrop(i)}
                    className="relative aspect-[4/3] border border-slate-200 bg-slate-100 group cursor-move"
                    data-testid={`img-tile-${i}`}
                  >
                    <img src={absoluteUrl(img)} alt={`${i}`} className="w-full h-full object-cover pointer-events-none" />

                    {/* Position number */}
                    <span className="absolute top-1 left-1 bg-slate-900 text-white text-[11px] font-medium px-1.5 py-0.5">
                      {i + 1}
                    </span>

                    {/* Cover badge */}
                    {i === 0 && (
                      <span className="absolute bottom-1 left-1 bg-amber-500 text-slate-900 text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-semibold flex items-center gap-1">
                        <Star size={9} /> Titelbild
                      </span>
                    )}

                    {/* Drag handle */}
                    <span className="absolute top-1 right-9 bg-white/90 p-1 text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical size={12} />
                    </span>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => removeItem("images", i)}
                      className="absolute top-1 right-1 bg-white border border-slate-300 p-1 hover:bg-red-600 hover:text-white"
                      data-testid={`img-remove-${i}`}
                    >
                      <X size={12} />
                    </button>

                    {/* Hover controls — move buttons (mobile-friendly) */}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/80 to-transparent p-2 flex items-center justify-between gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveImage(i, i - 1)}
                          disabled={i === 0}
                          className="bg-white/90 p-1 text-slate-700 disabled:opacity-30 hover:bg-white"
                          data-testid={`img-up-${i}`}
                          title="Nach vorne"
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(i, i + 1)}
                          disabled={i === form.images.length - 1}
                          className="bg-white/90 p-1 text-slate-700 disabled:opacity-30 hover:bg-white"
                          data-testid={`img-down-${i}`}
                          title="Nach hinten"
                        >
                          <ArrowDown size={12} />
                        </button>
                      </div>
                      {i !== 0 && (
                        <button
                          type="button"
                          onClick={() => setAsCover(i)}
                          className="text-[10px] uppercase tracking-widest bg-amber-500 text-slate-900 px-2 py-1 hover:bg-amber-400 font-semibold"
                          data-testid={`img-cover-${i}`}
                        >
                          Als Titelbild
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>

        <Section title="Ausstattung">
          <div className="flex gap-2">
            <input value={newEq} onChange={(e) => setNewEq(e.target.value)} data-testid="f-neweq" placeholder="z.B. Navigationssystem"
              className="flex-1 border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900 bg-white" />
            <button type="button" onClick={() => addItem("equipment", newEq, setNewEq)} data-testid="f-addeq" className="bg-slate-900 text-white px-4 text-sm hover:bg-red-600 transition-crisp">
              <Plus size={16} />
            </button>
          </div>
          {form.equipment?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {form.equipment.map((eq, i) => (
                <span key={i} className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 text-xs">
                  {eq}
                  <button type="button" onClick={() => removeItem("equipment", i)} className="text-slate-400 hover:text-red-600"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </Section>

        <Section title="Status">
          <div className="space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">Besteuerung</label>
              <div className="grid grid-cols-2 gap-2">
                <label className={`flex items-start gap-2 p-3 border cursor-pointer transition-crisp ${form.tax_type === "differenz" ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <input type="radio" name="tax_type" value="differenz" checked={form.tax_type === "differenz"} onChange={() => set("tax_type", "differenz")} data-testid="f-tax-differenz" className="mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">Differenzbesteuert</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">§ 24 UStG · USt. nicht ausweisbar</div>
                  </div>
                </label>
                <label className={`flex items-start gap-2 p-3 border cursor-pointer transition-crisp ${form.tax_type === "regel" ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <input type="radio" name="tax_type" value="regel" checked={form.tax_type === "regel"} onChange={() => set("tax_type", "regel")} data-testid="f-tax-regel" className="mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">MwSt. ausweisbar</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Regelbesteuerung · Vorsteuerabzug möglich</div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-6 pt-2 border-t border-slate-100">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="f-featured" />
                Empfohlen (Startseite)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.sold} onChange={(e) => set("sold", e.target.checked)} data-testid="f-sold" />
                Verkauft
              </label>
            </div>
          </div>
        </Section>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose} className="border border-slate-300 px-6 py-2.5 text-sm hover:bg-slate-100 transition-crisp">Abbrechen</button>
          <button type="submit" disabled={saving} data-testid="f-submit" className="bg-slate-900 text-white px-8 py-2.5 text-sm font-medium hover:bg-red-600 transition-crisp disabled:opacity-50">
            {saving ? "Speichere…" : vehicle ? "Aktualisieren" : "Anlegen"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 p-6">
      <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-slate-900 mb-5">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900 bg-white" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-slate-900 bg-white">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
